import React, { useState, useEffect, useCallback } from 'react';
import axios from '@/utils/api';
import { API } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Mail,
  MousePointer,
  Eye,
  TrendingUp,
  TrendingDown,
  Users,
  Send,
  Calendar,
  Clock,
  BarChart3,
  RefreshCw,
  Loader2,
  ChevronRight,
  X,
  ExternalLink,
  UserMinus,
  AlertTriangle,
  CheckCircle,
  Target,
  Sparkles
} from 'lucide-react';
import ScheduledCampaigns from './ScheduledCampaigns';

// Simple bar chart component - extracted outside to prevent re-renders
const MiniBarChart = ({ data, maxValue, color }) => {
  if (!data || data.length === 0) return null;
  const max = maxValue || Math.max(...data.map(d => d.value), 1);
  
  return (
    <div data-testid="campaign-analytics-mini-bar-chart" className="flex items-end gap-1 h-16">
      {data.map((item, idx) => {
        const heightPercent = max > 0 ? (item.value / max) * 100 : 0;
        const heightPx = Math.max(item.value > 0 ? 4 : 0, (heightPercent / 100) * 64); // 64px = h-16
        return (
          <div key={idx} data-testid="campaign-analytics-chart-bar" className="flex-1 flex flex-col justify-end h-full">
            <div 
              className="w-full rounded-t transition-all"
              style={{ 
                height: `${heightPx}px`,
                backgroundColor: color
              }}
              title={`${item.label}: ${item.value}`}
            />
          </div>
        );
      })}
    </div>
  );
};

const CampaignAnalytics = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email/analytics`);
      if (res.data.success) {
        setAnalytics(res.data);
      } else {
        toast.error(res.data.error || 'Failed to load analytics');
      }
    } catch (error) {
      toast.error('Failed to load analytics');
      console.error('Analytics error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCampaignDetails = useCallback(async (campaignId) => {
    setLoadingDetails(true);
    try {
      const res = await axios.get(`${API}/admin/email/analytics/${campaignId}`);
      if (res.data.success) {
        setCampaignDetails(res.data);
      } else {
        toast.error(res.data.error || 'Failed to load campaign details');
        setSelectedCampaign(null);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Campaign not found');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to load campaign details');
      }
      setSelectedCampaign(null);
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    if (selectedCampaign) {
      fetchCampaignDetails(selectedCampaign.campaign_id);
    } else {
      setCampaignDetails(null);
    }
  }, [selectedCampaign, fetchCampaignDetails]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return 'N/A';
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  };

  // Calculate open/click rates for a campaign
  const getCampaignRates = (campaign) => {
    const sent = campaign.sent_count || campaign.total_recipients || 0;
    const uniqueOpens = campaign.unique_opens?.length || 0;
    const uniqueClicks = campaign.unique_clicks?.length || 0;
    
    return {
      openRate: sent > 0 ? ((uniqueOpens / sent) * 100).toFixed(1) : 0,
      clickRate: sent > 0 ? ((uniqueClicks / sent) * 100).toFixed(1) : 0,
      clickToOpenRate: uniqueOpens > 0 ? ((uniqueClicks / uniqueOpens) * 100).toFixed(1) : 0
    };
  };

  if (loading) {
    return (
      <div data-testid="campaign-analytics-loading" className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl p-8">
          <Loader2 className="w-8 h-8 animate-spin text-[#df1283] mx-auto" />
          <p className="text-white/60 mt-4">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Campaign Details View
  if (selectedCampaign && campaignDetails) {
    const rates = getCampaignRates(campaignDetails.campaign);
    const campaign = campaignDetails.campaign;
    
    return (
      <div data-testid="campaign-analytics-detail-modal" className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl w-full max-w-4xl mb-8">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[#4A0A66]/50">
            <div className="flex items-center gap-3">
              <button
                data-testid="campaign-analytics-back"
                aria-label="Back to campaign analytics"
                onClick={() => setSelectedCampaign(null)}
                className="p-2 text-white/50 hover:text-white hover:bg-[#4A0A66]/50 rounded-lg"
              >
                <ChevronRight className="w-5 h-5 rotate-180" />
              </button>
              <div>
                <h3 className="text-lg font-semibold text-white">{campaign.subject}</h3>
                <p className="text-xs text-white/50">
                  {campaign.campaign_name || 'Campaign'} • Sent {formatDate(campaign.sent_at || campaign.created_at)}
                </p>
              </div>
            </div>
            <button 
              data-testid="campaign-analytics-detail-close"
              aria-label="Close campaign analytics"
              onClick={onClose}
              className="p-2 text-white/50 hover:text-white hover:bg-[#4A0A66]/50 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Key Metrics */}
            <div data-testid="campaign-analytics-detail-summary" className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
                <Send className="w-5 h-5 mx-auto mb-2 text-[#FF7901]" />
                <div className="text-2xl font-bold text-white">{campaign.sent_count || 0}</div>
                <div className="text-xs text-white/50">Sent</div>
              </div>
              <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
                <Eye className="w-5 h-5 mx-auto mb-2 text-[#0FFFE2]" />
                <div className="text-2xl font-bold text-white">{campaignDetails.unique_opens || 0}</div>
                <div className="text-xs text-white/50">Unique Opens</div>
                <div className="text-sm text-[#0FFFE2] font-medium">{rates.openRate}%</div>
              </div>
              <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
                <MousePointer className="w-5 h-5 mx-auto mb-2 text-[#df1283]" />
                <div className="text-2xl font-bold text-white">{campaignDetails.unique_clicks || 0}</div>
                <div className="text-xs text-white/50">Unique Clicks</div>
                <div className="text-sm text-[#df1283] font-medium">{rates.clickRate}%</div>
              </div>
              <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
                <Target className="w-5 h-5 mx-auto mb-2 text-[#FFD700]" />
                <div className="text-2xl font-bold text-white">{rates.clickToOpenRate}%</div>
                <div className="text-xs text-white/50">Click-to-Open</div>
              </div>
            </div>

            {/* Engaged Users */}
            {campaignDetails.engaged_users && campaignDetails.engaged_users.length > 0 && (
              <div data-testid="campaign-analytics-engaged-users" className="bg-[#4A0A66]/10 rounded-xl p-4">
                <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#0FFFE2]" />
                  Engaged Users ({campaignDetails.engaged_users.length})
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {campaignDetails.engaged_users.map((user, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-2 p-2 bg-[#4A0A66]/20 rounded-lg"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center text-white text-xs font-bold">
                        {(user.first_name || user.name || user.email)?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">
                          {user.first_name || user.name?.split(' ')[0] || 'User'}
                        </div>
                        <div className="text-xs text-white/50 truncate">{user.email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Events */}
            {campaignDetails.events && campaignDetails.events.length > 0 && (
              <div data-testid="campaign-analytics-recent-activity" className="bg-[#4A0A66]/10 rounded-xl p-4">
                <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#FF7901]" />
                  Recent Activity
                </h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {campaignDetails.events.slice(0, 20).map((event, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 p-2 bg-[#4A0A66]/20 rounded-lg"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        event.event_type === 'open' ? 'bg-[#0FFFE2]/20' :
                        event.event_type === 'click' ? 'bg-[#df1283]/20' :
                        'bg-[#FF7901]/20'
                      }`}>
                        {event.event_type === 'open' && <Eye className="w-4 h-4 text-[#0FFFE2]" />}
                        {event.event_type === 'click' && <MousePointer className="w-4 h-4 text-[#df1283]" />}
                        {event.event_type === 'bounce' && <AlertTriangle className="w-4 h-4 text-[#FF7901]" />}
                      </div>
                      <div className="flex-1">
                        <span className="text-sm text-white capitalize">{event.event_type}</span>
                        <span className="text-xs text-white/50 ml-2">
                          {formatTime(event.timestamp)} • {formatDate(event.timestamp)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Campaign Info */}
            {campaign.segment && (
              <div className="bg-[#4A0A66]/10 rounded-xl p-4">
                <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-[#df1283]" />
                  Segment Configuration
                </h4>
                <div className="text-sm text-white/70">
                  <span className="capitalize">{campaign.segment.segment_type || 'All'}</span>
                  {campaign.segment.tiers && (
                    <span> • Tiers: {campaign.segment.tiers.join(', ')}</span>
                  )}
                  {campaign.segment.active_in_days && (
                    <span> • Active in {campaign.segment.active_in_days} days</span>
                  )}
                  {campaign.segment.exclude_b2b && (
                    <span> • B2B excluded</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main Analytics Dashboard
  return (
    <div data-testid="campaign-analytics-modal" className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
      <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl w-full max-w-5xl mb-8">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#4A0A66]/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Campaign Analytics</h3>
              <p className="text-xs text-white/50">Track opens, clicks, and conversions</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="campaign-analytics-refresh"
              aria-label="Refresh campaign analytics"
              onClick={fetchAnalytics}
              variant="outline"
              size="sm"
              className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <button 
              data-testid="campaign-analytics-close"
              aria-label="Close campaign analytics"
              onClick={onClose}
              className="p-2 text-white/50 hover:text-white hover:bg-[#4A0A66]/50 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {analytics && (
          <div className="p-6 space-y-6">
            {/* Summary Stats */}
            <div data-testid="campaign-analytics-summary" className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="bg-gradient-to-br from-[#df1283]/20 to-[#FF7901]/20 rounded-xl p-4 border border-[#df1283]/30">
                <div className="flex items-center gap-2 mb-2">
                  <Send className="w-4 h-4 text-[#FF7901]" />
                  <span className="text-xs text-white/60">Total Sent</span>
                </div>
                <div className="text-2xl font-bold text-white">{analytics.summary.total_sent.toLocaleString()}</div>
              </div>
              
              <div className="bg-[#0FFFE2]/10 rounded-xl p-4 border border-[#0FFFE2]/30">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-4 h-4 text-[#0FFFE2]" />
                  <span className="text-xs text-white/60">Total Opens</span>
                </div>
                <div className="text-2xl font-bold text-white">{analytics.summary.total_opens.toLocaleString()}</div>
                <div className="text-sm text-[#0FFFE2]">{analytics.summary.open_rate}% rate</div>
              </div>
              
              <div className="bg-[#df1283]/10 rounded-xl p-4 border border-[#df1283]/30">
                <div className="flex items-center gap-2 mb-2">
                  <MousePointer className="w-4 h-4 text-[#df1283]" />
                  <span className="text-xs text-white/60">Total Clicks</span>
                </div>
                <div className="text-2xl font-bold text-white">{analytics.summary.total_clicks.toLocaleString()}</div>
                <div className="text-sm text-[#df1283]">{analytics.summary.click_rate}% rate</div>
              </div>

              <div className="bg-[#4A0A66]/20 rounded-xl p-4 border border-[#4A0A66]/50">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4 text-white/60" />
                  <span className="text-xs text-white/60">Campaigns</span>
                </div>
                <div className="text-2xl font-bold text-white">{analytics.summary.total_campaigns}</div>
              </div>

              <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <UserMinus className="w-4 h-4 text-amber-400" />
                  <span className="text-xs text-white/60">Unsubscribed</span>
                </div>
                <div className="text-2xl font-bold text-white">{analytics.summary.total_unsubscribes}</div>
              </div>

              <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-xs text-white/60">Bounces</span>
                </div>
                <div className="text-2xl font-bold text-white">{analytics.summary.total_bounces}</div>
                <div className="text-sm text-red-400">{analytics.summary.bounce_rate}% rate</div>
              </div>
            </div>

            {/* Scheduled Campaigns */}
            <ScheduledCampaigns onRefresh={fetchAnalytics} />

            {/* Daily Activity Chart */}
            {analytics.daily_stats && analytics.daily_stats.length > 0 && (
              <div data-testid="campaign-analytics-daily-activity" className="bg-[#4A0A66]/10 rounded-xl p-4">
                <h4 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#0FFFE2]" />
                  Daily Activity (Last 14 Days)
                </h4>
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded bg-[#0FFFE2]"></div>
                      <span className="text-xs text-white/60">Opens</span>
                    </div>
                    <MiniBarChart 
                      data={analytics.daily_stats.slice(-14).map(d => ({ label: d.date, value: d.opens || 0 }))}
                      color="#0FFFE2"
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-white/40">{analytics.daily_stats.slice(-14)[0]?.date?.slice(5) || ''}</span>
                      <span className="text-xs text-white/40">{analytics.daily_stats.slice(-1)[0]?.date?.slice(5) || ''}</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded bg-[#df1283]"></div>
                      <span className="text-xs text-white/60">Clicks</span>
                    </div>
                    <MiniBarChart 
                      data={analytics.daily_stats.slice(-14).map(d => ({ label: d.date, value: d.clicks || 0 }))}
                      color="#df1283"
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-white/40">{analytics.daily_stats.slice(-14)[0]?.date?.slice(5) || ''}</span>
                      <span className="text-xs text-white/40">{analytics.daily_stats.slice(-1)[0]?.date?.slice(5) || ''}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Campaigns */}
            <div data-testid="campaign-analytics-recent-campaigns" className="bg-[#4A0A66]/10 rounded-xl p-4">
              <h4 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#FF7901]" />
                Recent Campaigns
              </h4>
              {analytics.campaigns && analytics.campaigns.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {analytics.campaigns.map((campaign, idx) => {
                    const rates = getCampaignRates(campaign);
                    return (
                      <div 
                        key={idx}
                        data-testid={`campaign-analytics-campaign-${campaign.campaign_id}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedCampaign(campaign)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setSelectedCampaign(campaign);
                          }
                        }}
                        className="flex items-center gap-4 p-3 bg-[#4A0A66]/20 rounded-lg hover:bg-[#4A0A66]/40 cursor-pointer transition-colors group"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white truncate group-hover:text-[#0FFFE2]">
                            {campaign.subject || campaign.campaign_name || 'Untitled Campaign'}
                          </div>
                          <div className="text-xs text-white/50">
                            {formatDate(campaign.sent_at || campaign.created_at)} • 
                            {campaign.sent_count || campaign.total_recipients || 0} recipients
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs">
                          <div className="text-center">
                            <div className="text-[#0FFFE2] font-medium">{rates.openRate}%</div>
                            <div className="text-white/40">open</div>
                          </div>
                          <div className="text-center">
                            <div className="text-[#df1283] font-medium">{rates.clickRate}%</div>
                            <div className="text-white/40">click</div>
                          </div>
                          <div className={`px-2 py-1 rounded text-xs ${
                            campaign.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                            campaign.status === 'sending' ? 'bg-amber-500/20 text-amber-400' :
                            campaign.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                            'bg-[#4A0A66]/30 text-white/60'
                          }`}>
                            {campaign.status || 'sent'}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-white/50">
                  <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No campaigns sent yet</p>
                  <p className="text-sm">Send your first bulk email campaign to see analytics here</p>
                </div>
              )}
            </div>

            {/* Recent Events Activity */}
            {analytics.recent_events && analytics.recent_events.length > 0 && (
              <div data-testid="campaign-analytics-live-activity" className="bg-[#4A0A66]/10 rounded-xl p-4">
                <h4 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#FFD700]" />
                  Live Activity Feed
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                  {analytics.recent_events.slice(0, 15).map((event, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-2 p-2 bg-[#4A0A66]/20 rounded-lg"
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        event.event_type === 'open' ? 'bg-[#0FFFE2]/20' :
                        event.event_type === 'click' ? 'bg-[#df1283]/20' :
                        'bg-[#FF7901]/20'
                      }`}>
                        {event.event_type === 'open' && <Eye className="w-3 h-3 text-[#0FFFE2]" />}
                        {event.event_type === 'click' && <MousePointer className="w-3 h-3 text-[#df1283]" />}
                        {event.event_type === 'bounce' && <AlertTriangle className="w-3 h-3 text-[#FF7901]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-white capitalize">{event.event_type}</span>
                        <span className="text-xs text-white/40 ml-1">{formatTime(event.timestamp)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignAnalytics;
